import { type VariantProps, cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * KORI `.btn`: ink primary, soft bone, outline, ghost, soft, destructive and link.
 * Focus uses the global :focus-visible ring from kori/base.css; no shadcn ring is added.
 */
const buttonVariants = cva(
  [
    "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap border font-sans font-semibold leading-none no-underline",
    "rounded-control transition-[background-color,border-color] duration-(--dur-fast) ease-(--ease-quiet)",
    "active:brightness-[0.93] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
    "aria-pressed:shadow-[inset_0_0_0_2px_var(--action)] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        default: "border-transparent bg-action text-action-text hover:bg-action-hover",
        soft: "border-transparent bg-accent-soft text-accent-soft-text hover:bg-accent-soft-hover",
        outline: "border-border-strong bg-transparent text-heading hover:border-heading hover:bg-surface-active",
        ghost: "border-transparent bg-transparent text-heading hover:bg-surface-active",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-surface-soft-hover",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-danger-ink",
        link: "h-auto min-h-0 border-transparent bg-transparent px-0 text-link underline underline-offset-[3px]",
      },
      size: {
        default: "h-(--control-h) min-h-[max(var(--control-h),var(--touch-min))] px-(--control-pad-x) text-sm",
        sm: "h-(--control-h-sm) min-h-[max(var(--control-h-sm),var(--touch-min))] px-3 text-xs",
        lg: "h-(--control-h-lg) min-h-[max(var(--control-h-lg),var(--touch-min))] px-6 text-base",
        icon: "size-(--control-h) min-h-[max(var(--control-h),var(--touch-min))] min-w-[max(var(--control-h),var(--touch-min))] px-0",
      },
    },
    compoundVariants: [{ variant: "link", className: "h-auto min-h-0 px-0" }],
    defaultVariants: { variant: "default", size: "default" },
  },
);

type ButtonProps = React.ComponentPropsWithoutRef<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

/**
 * forwardRef because the app runs React 18: Radix triggers (`asChild`) measure and focus the
 * button through its ref, which a React 18 function component would otherwise drop.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, type, ...props },
  ref,
) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      ref={ref}
      data-slot="button"
      type={asChild ? undefined : (type ?? "button")}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
});

export { Button, buttonVariants };
